import { useQuery } from "@tanstack/react-query";

import { components } from "@/@types/api";
import { INSIGHTS_BACKEND_URL } from "@/lib/axios";
import { QueryConfig } from "@/lib/react-query";
import {
  Scenario,
  ScenarioFilterTypeEnum,
  ScenarioType,
  Status,
  StatusEnum,
} from "@/pages/scenario-builder/types";
import { axiosApi } from "@/Utils";

type ScenarioDTO = components["schemas"]["NewScenarioDTO"];
type ScenariosResponse = components["schemas"]["NewGetScenariosResponse"];

interface GetScenariosParams {
  status?: StatusEnum[];
  name?: string | undefined;
  scenarioStatus?: StatusEnum[];
  scenarioType?: ScenarioFilterTypeEnum[];
}

export const getScenarios = (params?: GetScenariosParams): Promise<ScenariosResponse> => {
  return axiosApi.get("/api/v2/scenarios-new/v2", { params, baseURL: INSIGHTS_BACKEND_URL });
};

type QueryFnType = typeof getScenarios;

type UseScenariosOptions = {
  config?: QueryConfig<QueryFnType>;
  filters?: GetScenariosParams;
};

const transformScenarios = (ScenariosResponse: ScenariosResponse): Scenario[] => {
  return ScenariosResponse.scenarios.map((scenarioDTO) => {
    return convertScenarioDTOToScenario(scenarioDTO);
  });
};

export const useScenarios = ({ config, filters }: UseScenariosOptions = {}) => {
  const query = useQuery({
    ...config,
    queryKey: ["scenarios-new", filters],
    queryFn: async () => {
      // Only include name if it has a non-empty value
      const apiFilters = { ...filters };
      if (apiFilters?.name === "") {
        delete apiFilters.name;
      }

      return await getScenarios(apiFilters);
    },
    select: (data: ScenariosResponse) => {
      return transformScenarios(data);
    },
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  return query;
};

const convertScenarioDTOToScenario = (scenarioDTO: ScenarioDTO): Scenario => {
  return {
    uuid: scenarioDTO.uuid || "",
    name: scenarioDTO.name,
    targetNodeUUID: scenarioDTO.targetNodeUuid || "",
    brand: scenarioDTO.brand || "",
    spend: scenarioDTO.spend || 0,
    revenue: scenarioDTO.revenue || 0,
    roi: scenarioDTO.roi || 0, // FIXME: This can be null
    startDate: new Date(scenarioDTO.startDate || 0),
    endDate: new Date(scenarioDTO.endDate || 0),
    createdBy: scenarioDTO.createdBy,
    createdAt: new Date(scenarioDTO.createdAt),
    status: Status.fromString(scenarioDTO.scenarioStatus),
    statusMessage: scenarioDTO.statusMessage || "",
    type: ScenarioType.fromString(scenarioDTO.scenarioType),
    scenarioCount: scenarioDTO.scenarioCount || 0,
    geography: scenarioDTO.geography || null,
  };
};
